import { createSlice, isAnyOf } from "@reduxjs/toolkit";
import { v4 as uuidv4 } from "uuid";

import { NepaliDate } from "@zener/nepali-datepicker-react";

import {
  getNext,
  getParticularPage,
  getPrevious,
  getSaleReturn,
  handleSearch,
  getSaleReturnAdditionalChargeList,
  createSaleReturn,
  getSaleReturnById,
  approveSaleReturn,
  cancelSaleReturn,
} from "./thunk";
import { dateFormater } from "../../../../../../Utils/dateFormater";

const initialState = {
  saleReturns: [],
  edit: false,
  saleReturn: null,
  count: 0,
  pageNo: 1,
  next: null,
  previous: null,
  loading: false,
  loadingCreate: false,
  loadingUpdated: false,
  loadingSaleReturn: false,
  loadingNext: false,
  loadingDetails: false,
  saleInvoiceDetails: [
    {
      unique: uuidv4(),
      product: null,
      closingStock: "",
      description: "",
      actualQuantity: "",
      validateQuantity: 0,
      unit: "",
      altQuantity: "",
      altUnit: null,
      rate: "",
      amount: "",
      requiredDate: dateFormater(new NepaliDate().toADasDate()),
    },
  ],
  additionalCharges: null,
  saveAndContinue: false,
  showCreateModal: false,
  selectedSaleInvoice: null,
  selectedSaleInvoiceDetails: [],
};

export const saleReturnSlice = createSlice({
  name: "saleReturn",
  initialState,
  reducers: {
    setShowCreateModal: (state, action) => {
      state.showCreateModal = action.payload;
    },
    setSaveAndContinue: (state, { payload }) => {
      state.saveAndContinue = payload;
    },
    goToPage: (state, { payload }) => {
      state.pageNo = payload;
    },

    setSelectedInvoice: (state, action) => {
      state.selectedSaleInvoice = action.payload;
    },
    setSelectedInvoiceDetails: (state, action) => {
      state.selectedSaleInvoiceDetails = action.payload.map((value) => ({
        ...value,
        description: value.product?.name || "",
      }));
    },
    setSaleInvoice: (state, action) => {
      state.saleInvoice = action.payload;
    },

    saleInvoiceDetailsUpdated: (state, action) => {
      state.saleInvoiceDetails = action.payload;
    },
    updatedAdditionalCharges: (state, action) => {
      state.additionalCharges = action.payload;
    },

    setSaleInvoiceDetails: (state, action) => {
      state.saleInvoiceDetails = [
        ...action.payload.map((value) => ({
          ...value,
          description: value.product?.name || "",
          actualQuantity: value.actualQuantity - value.returnQuantity,
          validateQuantity: value.actualQuantity - value.returnQuantity,
          amount: value.rate * (value.actualQuantity - value.returnQuantity),
        })),
        {
          unique: uuidv4(),
          product: null,
          closingStock: "",
          description: "",
          actualQuantity: "",
          validateQuantity: 0,
          unit: "",
          altQuantity: "",
          altUnit: null,
          rate: "",
          amount: "",
          requiredDate: dateFormater(new NepaliDate().toADasDate()),
        },
      ];
    },

    clearSelectedOrderData: (state) => {
      state.selectedSaleInvoice = null;
      state.selectedSaleInvoiceDetails = [];
    },

    clearSaleInvoiceDetails: (state) => {
      state.saleInvoiceDetails = [
        {
          unique: uuidv4(),
          product: null,
          closingStock: "",
          description: "",
          actualQuantity: "",
          validateQuantity: 0,
          unit: "",
          altQuantity: "",
          altUnit: null,
          rate: "",
          amount: "",
          requiredDate: dateFormater(new NepaliDate().toADasDate()),
        },
      ];
    },
    clearAllSaleReturn: (state) => {
      state.edit = false;
      state.loading = false;
      state.loadingUpdated = false;
      state.loadingSaleReturn = false;
      state.saleReturn = null;
      state.selectedSaleInvoiceDetails = [];
      state.saleInvoiceDetails = [
        {
          unique: uuidv4(),
          product: null,
          closingStock: "",
          description: "",
          actualQuantity: "",
          validateQuantity: 0,
          unit: "",
          altQuantity: "",
          altUnit: null,
          rate: "",
          amount: "",
          requiredDate: dateFormater(new NepaliDate().toADasDate()),
        },
      ];
      state.additionalCharges = null;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getSaleReturnAdditionalChargeList.pending, (state) => {
      state.additionalCharges = null;
    });
    builder.addCase(getSaleReturnAdditionalChargeList.fulfilled, (state, action) => {
      state.additionalCharges = action.payload.data;
    });
    builder.addCase(getSaleReturnAdditionalChargeList.rejected, (state) => {
      state.additionalCharges = null;
    });
    builder.addCase(createSaleReturn.pending, (state) => {
      state.loadingCreate = true;
    });
    builder.addCase(createSaleReturn.fulfilled, (state, action) => {
      state.loadingCreate = false;
    });
    builder.addCase(createSaleReturn.rejected, (state) => {
      state.loadingCreate = false;
    });
    // getSaleReturnById
    builder.addCase(getSaleReturnById.pending, (state) => {
      state.loading = true;
      state.saleReturn = null;
    });
    builder.addCase(getSaleReturnById.fulfilled, (state, action) => {
      state.edit = true;
      state.loading = false;
      state.saleReturn = action.payload.data;
      state.saleInvoiceDetails = action.payload.data?.salesOrderReturnDetails.map((value) => {
        return { ...value, unique: uuidv4() };
      });
    });
    builder.addCase(getSaleReturnById.rejected, (state) => {
      state.edit = false;
      state.loading = false;
      state.saleReturn = null;
    });
    builder.addMatcher(
      isAnyOf((createSaleReturn.pending, approveSaleReturn.pending, cancelSaleReturn.pending), (state) => {
        state.loadingSaleReturn = true;
      })
    );
    builder.addMatcher(
      isAnyOf((createSaleReturn.fulfilled, approveSaleReturn.fulfilled, cancelSaleReturn.fulfilled), (state) => {
        state.loadingSaleReturn = true;
      })
    );
    builder.addMatcher(
      isAnyOf((createSaleReturn.rejected, approveSaleReturn.rejected, cancelSaleReturn.rejected), (state) => {
        state.loadingSaleReturn = true;
      })
    );
    builder.addMatcher(
      isAnyOf(
        getSaleReturn.pending,
        getParticularPage.pending,
        getPrevious.pending,
        getNext.pending,
        handleSearch.pending
      ),
      (state) => {
        state.loadingSaleReturn = true;
      }
    );

    builder.addMatcher(
      isAnyOf(
        getSaleReturn.fulfilled,
        getParticularPage.fulfilled,
        getPrevious.fulfilled,
        getNext.fulfilled,
        handleSearch.fulfilled
      ),
      (state, action) => {
        state.loadingSaleReturn = false;
        state.saleReturns = action.payload.data;
        state.count = action.payload.totalCount;

        state.previous = action.payload.previous;
        state.next = action.payload.next;
      }
    );
    builder.addMatcher(
      isAnyOf(
        getSaleReturn.rejected,
        getParticularPage.rejected,
        getPrevious.rejected,
        getNext.rejected,
        handleSearch.rejected
      ),
      (state) => {
        state.loadingSaleReturn = false;
      }
    );
  },
});

export const {
  setShowCreateModal,
  setSaveAndContinue,
  goToPage,
  setSelectedInvoice,
  setSelectedInvoiceDetails,
  saleInvoiceDetailsUpdated,
  setSaleInvoiceDetails,
  updatedAdditionalCharges,
  clearSelectedOrderData,
  clearSaleInvoiceDetails,
  clearAllSaleReturn,
} = saleReturnSlice.actions;

export default saleReturnSlice.reducer;
